/**
 * Axios Offline-First Interceptor (Phase 1)
 *
 * المبدأ:
 *  - GET ناجح: يُخزّن في api_cache + يُحدّث جداول local_<entity> (seed محلي)
 *  - GET فاشل (Offline): يُرجع من api_cache + يدمج local pending
 *  - POST/PUT/DELETE فاشل (Offline) لـ entities المعروفة:
 *       1. يُحفظ محلياً بـ offline_id فوراً (يظهر في القوائم مباشرة)
 *       2. يدخل sync_queue لرفعه عند رجوع الاتصال
 *  - عند الـ replay الناجح:
 *       1. خريطة id_map (offline_X → real_id)
 *       2. يُعاد كتابة ID الكيان المحلي
 *       3. يُعاد كتابة أي مرجع لـ offline_X في باقي عناصر sync_queue
 */
import axios from 'axios';
import {
  cacheGet, getCached,
  enqueueSync, getPendingQueue, updateQueueItem, removeQueueItem, getPendingCount,
  mapLocalToReal, resolveId,
  offlineDB,
} from './db';
import {
  detectEntity, genOfflineId,
  saveLocalEntity, seedLocalList, seedLocalEntity,
  getLocalEntity, mergeListWithPending, rewriteLocalId,
  ENTITY_TYPES,
} from './entityMap';
import { computeAccountStatementOffline, parseQueryParams } from './statementCalc';

const OFFLINE_FLAG = 'X-Offline-Queued';

// Endpoints التي يمكن queue-ها (نفس القائمة الموسعة)
const QUEUEABLE_ENDPOINTS = [
  '/sales', '/customers', '/products', '/purchases', '/suppliers',
  '/customer-payments', '/supplier-payments', '/returns', '/expenses',
  '/damages', '/employees', '/cash-register',
  '/held-invoices', '/reminders', '/multi-vouchers',
  '/installments', '/installment-payments',
  '/settings', '/store-settings', '/labels', '/loyalty', '/payments',
];

function isQueueable(url, method, data) {
  if (!['post', 'put', 'patch', 'delete'].includes(method?.toLowerCase())) return false;
  if (data instanceof FormData) return false;
  if (typeof data === 'string' && data.startsWith('---')) return false;
  if (url.match(/\/(image|images|upload|files|avatar|photo)/i)) return false;
  return QUEUEABLE_ENDPOINTS.some(ep => url.includes(ep));
}

// ============ Event Bus ============
const listeners = new Set();
export function onOfflineChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
async function notifyChange() {
  const count = await getPendingCount();
  listeners.forEach(cb => {
    try { cb({ pendingCount: count, online: navigator.onLine }); } catch {}
  });
}

// ============ كشف Offline من response ============
function detectOfflineFromResponse(error) {
  if (!error.response) return true;
  const r = error.response;
  if (r.status === 503 && r.data && (r.data.offline === true || r.data.error === 'لا يوجد اتصال بالإنترنت')) {
    return true;
  }
  return false;
}

// ============ تحويل payload لـ object ============
function normalizePayload(data) {
  if (typeof data === 'string') {
    try { return JSON.parse(data); } catch { return data; }
  }
  return data;
}

// ============ ID rewriting في كائن (recursive) ============
async function rewriteOfflineIdsInPayload(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return Promise.all(obj.map(rewriteOfflineIdsInPayload));
  }
  const out = { ...obj };
  for (const k of Object.keys(out)) {
    const v = out[k];
    if (typeof v === 'string' && v.startsWith('offline_')) {
      out[k] = await resolveId(v);
    } else if (v && typeof v === 'object') {
      out[k] = await rewriteOfflineIdsInPayload(v);
    }
  }
  return out;
}

// ============ Install Interceptors ============
export function installOfflineInterceptors() {
  // ---------- Response interceptor ----------
  axios.interceptors.response.use(
    async (response) => {
      try {
        const cfg = response.config;
        const method = cfg.method?.toLowerCase();
        const url = cfg.url || '';

        // 1. كاش GET ناجح
        if (method === 'get' && url.includes('/api/')) {
          await cacheGet(url, response.data);

          // 2. seed local entity tables من الـ response
          const ent = detectEntity(url);
          if (ent) {
            if (ent.scope === 'list' && Array.isArray(response.data)) {
              await seedLocalList(ent.entityType, response.data);
              // 3. merge pending offline items into list before returning
              response.data = await mergeListWithPending(ent.entityType, response.data);
            } else if (ent.scope === 'single' && response.data && response.data.id) {
              await seedLocalEntity(ent.entityType, response.data);
            }
          }

          // 4. nested patterns: customer/{id}/account-statement, debts, etc.
          // نتركها للأن — يكفي merging /api/sales لأن الصفحة تفلتر client-side
        }
      } catch (e) { /* ignore */ }
      return response;
    },

    // ---------- Error path: Offline fallback ----------
    async (error) => {
      const cfg = error.config || {};
      const url = cfg.url || '';
      const method = (cfg.method || 'get').toLowerCase();
      const isNetworkError = detectOfflineFromResponse(error);

      if (isNetworkError && url.includes('/api/')) {
        // ===== كشف الحساب — يُحسب محلياً من Dexie =====
        if (method === 'get' && url.includes('/account-statement')) {
          try {
            const params = { ...(cfg.params || {}), ...parseQueryParams(url) };
            const statement = await computeAccountStatementOffline({
              type: params.type,
              entity_id: params.entity_id,
              date_from: params.date_from,
              date_to: params.date_to,
            });
            console.log('[Offline] Computed account-statement locally for', params.type, params.entity_id);
            return Promise.resolve({
              data: statement, status: 200, statusText: 'OK (Local Computed)',
              headers: { 'x-offline-cache': 'computed' }, config: cfg,
            });
          } catch (e) {
            console.warn('[Offline] account-statement compute failed:', e);
          }
        }

        // ===== GET Offline =====
        if (method === 'get') {
          // محاولة استرجاع من api_cache
          let cached = await getCached(url);
          const ent = detectEntity(url);

          if (cached !== null) {
            console.log('[Offline] Returning cached GET:', url);
            // merge pending offline items
            if (ent && ent.scope === 'list' && Array.isArray(cached)) {
              cached = await mergeListWithPending(ent.entityType, cached);
            }
            return Promise.resolve({
              data: cached, status: 200, statusText: 'OK (Cached)',
              headers: { 'x-offline-cache': 'true' }, config: cfg,
            });
          }

          // لا كاش — لكن قد يكون هناك pending offline (مثلاً أول استخدام)
          if (ent && ent.scope === 'list') {
            const merged = await mergeListWithPending(ent.entityType, []);
            return Promise.resolve({
              data: merged, status: 200, statusText: 'OK (Local Only)',
              headers: { 'x-offline-cache': 'local-only' }, config: cfg,
            });
          }

          // single offline id؟
          if (ent && ent.scope === 'single' && ent.id?.startsWith('offline_')) {
            const local = await getLocalEntity(ent.entityType, ent.id);
            if (local) {
              return Promise.resolve({
                data: local, status: 200, statusText: 'OK (Local)',
                headers: { 'x-offline-cache': 'local' }, config: cfg,
              });
            }
          }
        }

        // ===== POST/PUT/DELETE Offline =====
        if (isQueueable(url, method, cfg.data)) {
          let payload = normalizePayload(cfg.data) || {};
          let localId = null;
          let optimisticData = { ...(typeof payload === 'object' ? payload : {}) };
          let entForQueue = null;

          // إثراء Payload: ضمان supplier_name/customer_name لو ناقص (للمزامنة اللاحقة)
          if (method === 'post' && typeof payload === 'object') {
            if (url.includes('/api/purchases') && payload.supplier_id && !payload.supplier_name) {
              const sup = await offlineDB.local_suppliers.get(payload.supplier_id);
              if (sup?.name) { payload = { ...payload, supplier_name: sup.name }; optimisticData = { ...optimisticData, supplier_name: sup.name }; }
            }
            if (url.includes('/api/sales') && payload.customer_id && !payload.customer_name) {
              const cust = await offlineDB.local_customers.get(payload.customer_id);
              if (cust?.name) { payload = { ...payload, customer_name: cust.name }; optimisticData = { ...optimisticData, customer_name: cust.name }; }
            }
          }

          // حالة خاصة: POST /api/customers/{id}/payment → حفظ محلياً كـ customer_payment
          const custPayMatch = /\/api\/customers\/([^/]+)\/payment/.exec(url);
          const supPayMatch = /\/api\/suppliers\/([^/]+)\/payment/.exec(url);
          if (method === 'post' && (custPayMatch || supPayMatch)) {
            const isCust = !!custPayMatch;
            const parentId = isCust ? custPayMatch[1] : supPayMatch[1];
            const realParentId = await resolveId(parentId);
            localId = genOfflineId();
            const payRecord = await saveLocalEntity('customer_payment', {
              ...optimisticData,
              id: localId,
              [isCust ? 'customer_id' : 'supplier_id']: realParentId,
              type: isCust ? 'customer' : 'supplier',
              created_at: new Date().toISOString(),
            });
            optimisticData = payRecord;
            entForQueue = { entityType: isCust ? 'customer_payment' : 'supplier_payment' };
            console.log('[Offline-First] Saved local', isCust ? 'customer' : 'supplier', 'payment', localId, 'for', parentId);
          }
          // حالة عامة: POST لـ entity معروف
          else if (method === 'post' && detectEntity(url)?.scope === 'list') {
            const ent = detectEntity(url);
            localId = genOfflineId();
            const localRecord = await saveLocalEntity(ent.entityType, {
              ...optimisticData,
              id: localId,
              created_at: optimisticData.created_at || new Date().toISOString(),
            });
            optimisticData = localRecord;
            entForQueue = ent;
            console.log('[Offline-First] Saved local', ent.entityType, localId);
          }
          // PUT/PATCH لكيان موجود
          else if ((method === 'put' || method === 'patch') && detectEntity(url)?.scope === 'single') {
            const ent = detectEntity(url);
            await saveLocalEntity(ent.entityType, { ...optimisticData, id: ent.id });
            localId = ent.id;
            entForQueue = ent;
          }
          // DELETE لكيان
          else if (method === 'delete' && detectEntity(url)?.scope === 'single') {
            const ent = detectEntity(url);
            try {
              await offlineDB[ENTITY_TYPES[ent.entityType].table].delete(ent.id);
            } catch {}
            localId = ent.id;
            entForQueue = ent;
          }

          // أضف للـ sync_queue
          const queueItem = await enqueueSync(url, method, payload, {
            localId,
            entityType: entForQueue?.entityType || null,
          });
          await notifyChange();
          console.log('[Offline] Queued:', method.toUpperCase(), url, 'id:', queueItem.id, 'local:', localId);

          return Promise.resolve({
            data: {
              ...optimisticData,
              id: localId || optimisticData.id || `offline_${queueItem.id}`,
              _offline_pending: true,
              _queue_id: queueItem.id,
            },
            status: 202,
            statusText: 'Accepted (Queued offline)',
            headers: { [OFFLINE_FLAG]: 'true' },
            config: cfg,
          });
        }
      }
      return Promise.reject(error);
    }
  );

  // ---------- Sync triggers ----------
  window.addEventListener('online', () => {
    console.log('[Offline] Network back — syncing queue');
    syncQueue();
  });
  if (navigator.onLine) setTimeout(syncQueue, 2000);
  setInterval(() => {
    if (navigator.onLine) syncQueue();
    notifyChange();
  }, 60_000);
}

// ============ Sync Queue Runner ============
let syncing = false;

export async function syncQueue() {
  if (syncing || !navigator.onLine) return { synced: 0, failed: 0 };
  syncing = true;
  let synced = 0;
  let failed = 0;
  try {
    const items = await getPendingQueue();
    for (const item of items) {
      try {
        await updateQueueItem(item.id, { status: 'syncing' });

        // 1. أعد كتابة أي offline_X في URL أو payload إلى ID حقيقي إن وُجد
        let endpoint = item.endpoint;
        // مثال: /api/customers/offline_X في PUT/DELETE
        const offIdMatch = endpoint.match(/(offline_[a-z0-9_]+)/i);
        if (offIdMatch) {
          const real = await resolveId(offIdMatch[1]);
          if (real && real !== offIdMatch[1]) {
            endpoint = endpoint.replace(offIdMatch[1], real);
          } else if (real === offIdMatch[1]) {
            // الكيان الأصلي لم يُزامن بعد — ارجع لاحقاً
            await updateQueueItem(item.id, { status: 'pending', lastError: 'Awaiting parent sync' });
            continue;
          }
        }

        let payload = normalizePayload(item.payload);
        if (payload && typeof payload === 'object') {
          payload = await rewriteOfflineIdsInPayload(payload);
        }

        // 2. نفّذ الطلب الفعلي
        const resp = await axios({
          url: endpoint, method: item.method, data: payload,
          headers: { 'Content-Type': 'application/json', 'X-Offline-Replay': 'true' },
        });

        // 3. خريطة الـ ID + إعادة كتابة الكيان المحلي
        if (item.localId && item.entityType && resp?.data?.id && item.localId.startsWith('offline_')) {
          await mapLocalToReal(item.localId, resp.data.id, item.entityType);
          await rewriteLocalId(item.entityType, item.localId, resp.data.id, resp.data);
          console.log('[Sync] Mapped', item.localId, '→', resp.data.id);
        } else if (item.localId && item.entityType && resp?.data?.id) {
          // PUT — تحديث محلي بالقيم من السيرفر
          await seedLocalEntity(item.entityType, resp.data);
        }

        await removeQueueItem(item.id);
        synced++;
      } catch (err) {
        const attempts = (item.attempts || 0) + 1;
        await updateQueueItem(item.id, {
          status: attempts >= 5 ? 'failed' : 'pending',
          attempts,
          lastError: err?.response?.data?.detail || err?.message || 'Unknown error',
        });
        failed++;
      }
    }
    await notifyChange();
  } finally {
    syncing = false;
  }
  return { synced, failed };
}

export { getPendingCount };
